import { ReactNode } from 'react';

import { cn } from '@/app/shared/lib/cn';

export interface VaultsItemStat {
  label: string;
  value: string;
  secondary?: {
    label: string;
    value: string;
  };
}

interface VaultsItemPageContainerProps {
  hero: ReactNode;
  stats: VaultsItemStat[];
  children: ReactNode;
}

const LEFT_GRADIENT = 'linear-gradient(270deg, var(--decoration-stroke) 0%, var(--background) 100%)';
const RIGHT_GRADIENT = 'linear-gradient(90deg, var(--decoration-stroke) 0%, var(--background) 100%)';

const StripeRow = () => (
  <>
    <div className='border-decoration-stroke hidden h-2.5 border-b xl:block min-[1300px]:border-r' />
    <div className='border-decoration-stroke h-2.5 border-b' />
    <div className='border-decoration-stroke hidden h-2.5 border-b xl:block min-[1300px]:border-l' />
  </>
);

const StatValue = ({ value }: { value: string }) => {
  if (value.startsWith('$') && value.length > 1) {
    return (
      <p className='text-2xl leading-8 font-normal text-gray-200'>
        <span className='text-gray-500'>$</span>
        {value.slice(1)}
      </p>
    );
  }

  return <p className='text-2xl leading-8 font-normal text-gray-200'>{value}</p>;
};

const VaultsItemPageContainer = ({ hero, stats, children }: VaultsItemPageContainerProps) => {
  return (
    <div className='border-decoration-stroke flex min-h-[calc(100vh-230px)] flex-col md:min-h-[calc(100vh-138px)] xl:mx-5.75 xl:border-x'>
      <div className='grid min-h-0 min-w-0 flex-1 grid-cols-1 grid-rows-[minmax(320px,auto)_auto_auto_auto_1fr] xl:grid-cols-[1fr_minmax(0,1244px)_1fr]'>
        <div className='border-decoration-stroke hidden border-b xl:block min-[1300px]:border-r' />
        <div className='border-decoration-stroke min-h-80 border-b'>{hero}</div>
        <div className='border-decoration-stroke hidden border-b xl:block min-[1300px]:border-l' />

        <div className='border-decoration-stroke hidden h-25 border-b xl:block min-[1300px]:border-r'>
          <div className='hidden h-full w-full opacity-30 min-[1300px]:block' style={{ background: LEFT_GRADIENT }} />
        </div>
        <div className='border-decoration-stroke grid w-full min-w-0 grid-cols-1 border-b min-[1056px]:h-25 min-[1056px]:grid-cols-4'>
          {stats.map((item, index) => (
            <div
              key={item.label}
              className={cn(
                'border-decoration-stroke flex min-w-0 flex-col justify-between gap-1 py-4.5 pl-5 pr-5 xl:pl-10',
                index !== stats.length - 1 && 'border-b min-[1056px]:border-r min-[1056px]:border-b-0',
              )}
            >
              <div className='flex min-w-0 items-start justify-between gap-2'>
                <p className='min-w-0 truncate text-base leading-6 font-normal text-gray-400'>{item.label}</p>
                {item.secondary != null && (
                  <p className='shrink-0 truncate text-right text-sm leading-6 font-normal text-gray-400'>
                    {item.secondary.label}
                  </p>
                )}
              </div>
              <div className='flex min-w-0 items-baseline justify-between gap-2'>
                <StatValue value={item.value} />
                {item.secondary != null && (
                  <p className='shrink-0 truncate text-right text-base leading-6 font-normal text-gray-200'>
                    {item.secondary.value}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className='border-decoration-stroke hidden h-25 border-b xl:block min-[1300px]:border-l'>
          <div className='hidden h-full w-full opacity-30 min-[1300px]:block' style={{ background: RIGHT_GRADIENT }} />
        </div>

        <StripeRow />
        <StripeRow />

        <div className='border-decoration-stroke hidden xl:block min-[1300px]:border-r' />
        <div className='min-h-0 w-full min-w-0 px-5 py-10 xl:px-10'>{children}</div>
        <div className='border-decoration-stroke hidden xl:block min-[1300px]:border-l' />
      </div>
    </div>
  );
};

export default VaultsItemPageContainer;
